import { useEffect, useRef } from "react";
import find from "lodash/find";
import { Map as Maplibre, useMap } from "react-map-gl/maplibre";
import {
	GeoJSONStoreFeatures,
	TerraDraw,
	TerraDrawMapLibreGLAdapter,
	TerraDrawRectangleMode,
	TerraDrawPolygonMode,
	TerraDrawLineStringMode,
	TerraDrawCircleMode,
	TerraDrawPointMode,
	TerraDrawSelectMode,
} from "terra-draw";

import {
	useSetActive,
	useDrawingMode,
	useSetDrawingMode,
	useSketchGeojson,
	useSetSketchGeojson,
	useSetSelectFeatureId,
} from "~map-view/states/sketch";

export const useDrawEditor = () => {
	const setDrawingMode = useSetDrawingMode();
	const setActive = useSetActive();
	const setSelectFeatureId = useSetSelectFeatureId();
	const sketchGeojson = useSketchGeojson();
	const setSketchGeojson = useSetSketchGeojson();
	const drawingMode = useDrawingMode();

	// Ref to store the latest sketchGeojson
	const sketchGeojsonRef = useRef(sketchGeojson);

	// Initialize TerraDraw only once
	const drawInstance = useRef<TerraDraw | null>(null);
	const { default: map } = useMap();

	// Update the ref whenever sketchGeojson changes
	useEffect(() => {
		sketchGeojsonRef.current = sketchGeojson;
	}, [sketchGeojson]);

	const initSketch = (map, sketchGeojsonRef) => {
		if (!drawInstance.current) {
			const getFeatureProperties = (id: string) => {
				const feature = find(sketchGeojsonRef.current?.features, { id });
				return feature ? feature.properties : null;
			};

			const getStyle = () => {
				return {
					fillColor: (feature: GeoJSONStoreFeatures) =>
						getFeatureProperties(String(feature.id))?.style?.fillColour ||
						"#467BFF",
					fillOpacity: (feature: GeoJSONStoreFeatures) =>
						Number(
							getFeatureProperties(String(feature.id))?.style?.fillOpacity ||
								0.5,
						),
					outlineColor: (feature: GeoJSONStoreFeatures) =>
						getFeatureProperties(String(feature.id))?.style?.strokeColour ||
						"#467BFF",
					outlineWidth: (feature: GeoJSONStoreFeatures) =>
						Number(
							getFeatureProperties(String(feature.id))?.style?.strokeWidth || 3,
						),

					lineStringColor: (feature: GeoJSONStoreFeatures) =>
						getFeatureProperties(String(feature.id))?.style?.colour ||
						"#467BFF",
					lineStringWidth: (feature: GeoJSONStoreFeatures) =>
						Number(getFeatureProperties(String(feature.id))?.style?.width || 3),
				};
			};

			drawInstance.current = new TerraDraw({
				adapter: new TerraDrawMapLibreGLAdapter({
					map: map.getMap(),
					lib: Maplibre,
				}),
				// Add different drawing modes
				modes: [
					new TerraDrawPointMode(),
					new TerraDrawRectangleMode({
						styles: getStyle(),
					}),
					new TerraDrawPolygonMode({
						styles: getStyle(),
					}),
					new TerraDrawLineStringMode({
						styles: getStyle(),
					}),
					new TerraDrawCircleMode({
						styles: getStyle(),
					}),
					new TerraDrawSelectMode({
						dragEventThrottle: 0,
						flags: {
							polygon: {
								feature: {
									draggable: true,
									rotateable: true,
									scaleable: true,
									coordinates: {
										midpoints: true,
										draggable: true,
										deletable: true,
									},
								},
							},
							linestring: {
								feature: {
									draggable: true,
									coordinates: {
										midpoints: true,
										draggable: true,
										deletable: true,
									},
								},
							},
							rectangle: {
								feature: {
									draggable: true,
									coordinates: { draggable: true, resizable: "opposite" },
								},
							},
							circle: {
								feature: {
									draggable: true,
									coordinates: { draggable: true, resizable: "center" },
								},
							},
							point: { feature: { draggable: true } },
						},
					}),
				],
			});
			drawInstance.current.start(); // Start TerraDraw
		}

		return drawInstance.current;
	};

	const handleFeatureActivation = (feature, drawingMode) => {
		switch (feature?.properties?.mode) {
			case "rectangle":
				setActive("rectangle");
				break;
			case "polygon":
				setActive("polygon");
				break;
			case "circle":
				setActive("circle");
				break;
			case "linestring":
				setActive("linestring");
				break;
			case "point":
				setActive(drawingMode === "marker" ? "marker" : "text-annotation");
				break;
		}
	};

	useEffect(() => {
		if (map && drawingMode) {
			const draw = initSketch(map, sketchGeojsonRef);
			if (sketchGeojson) {
				// Clear the previous features
				draw?.clear();

				// Convert the features into GeoJSONStoreFeatures
				const convertedFeatures = sketchGeojson.features.map((feature) => ({
					...feature,
					properties: {
						...feature.properties,
						mode: feature.properties?.mode || "unknown", // Add mode if missing
					},
					geometry: feature.geometry,
					id: String(feature.id), // Ensure id is a string
				})) as GeoJSONStoreFeatures[]; // Cast to GeoJSONStoreFeatures

				// Add the converted features to TerraDraw
				draw?.addFeatures(convertedFeatures);
			}

			// Set the drawing mode
			if (drawingMode) {
				if (drawingMode === "close") {
					draw?.clear();
					return;
				}
				const mode =
					drawingMode === "marker" || drawingMode === "text-annotation"
						? "point"
						: drawingMode;
				draw?.setMode(mode);

				// Handle select and finish events
				draw.on("select", (featureId) => {
					const snapshot = draw.getSnapshot();
					const feature = snapshot.find((f) => f.id === featureId);
					handleFeatureActivation(feature, drawingMode);
				});

				draw.on("finish", (featureId) => {
					const snapshot = draw.getSnapshot();
					const feature = snapshot.find((f) => f.id === featureId);
					handleFeatureActivation(feature, drawingMode);
					setSelectFeatureId(String(featureId));
					setSketchGeojson({
						type: "FeatureCollection",
						features: snapshot.filter((f) => f.properties.mode !== "select"),
					});
					draw.setMode("select");
					setDrawingMode("select");
				});
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, drawingMode, sketchGeojson]);

	return null;
};
